import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useState, useEffect, SetStateAction, Dispatch, useCallback } from 'react';
import styles from './questionChecklist.module.css';
import { TGetAllQuestionsAndOptionsResponse } from '../../../services/types/user.types';
import QuestionItem from './questionItem';
import Loader from '../../loader/loader';
import Tooltip from '../../tooltip/tooltip';

const SelectAllIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
        <path d="M5 10.5L9 14.5L15 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const UnselectIcon = () => (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
        <path d="M6 6L14 14M14 6L6 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
);

interface QuestionChecklistProps {
    questionsWithOptions: TGetAllQuestionsAndOptionsResponse;
    title: string;
    setSelectedQuestions: Dispatch<SetStateAction<Record<number, number[]>>>;
    selectedQuestions: Record<number, number[]>;
    usersSelectedList: number[];
    onClose: () => void;
    onApply: () => void;
    isLoading?: boolean;
    /** Optional: questionnaire id -> name for section headers when multiple questionnaires */
    questionnaireNames?: Record<number, string>;
}


// Helpers
const getAllQuestionIds = (questionsWithOptions: TGetAllQuestionsAndOptionsResponse) => [
    ...questionsWithOptions.questionsOptions.map(item => item.Questions.Id),
    ...questionsWithOptions.questionsEmotions.map(item => item.Questions.Id)
];

const buildAllSelectedObj = (questionsWithOptions: TGetAllQuestionsAndOptionsResponse) => {
    const allSelectedObj: Record<number, number[]> = {};
    questionsWithOptions.questionsOptions.forEach(item => {
        allSelectedObj[item.Questions.Id] = item.QuestionsOptions.map(opt => opt.Id);
    });
    questionsWithOptions.questionsEmotions.forEach(item => {
        allSelectedObj[item.Questions.Id] = item.QuestionsEmotions.map(opt => opt.Id);
    });
    return allSelectedObj;
};

const areAllSelected = (
    selectedQuestions: Record<number, number[]>,
    questionsWithOptions: TGetAllQuestionsAndOptionsResponse
) => {
    const allIds = getAllQuestionIds(questionsWithOptions);
    if (Object.keys(selectedQuestions).length !== allIds.length) return false;
    return allIds.every(id => selectedQuestions[id]?.length > 0);
};

const QuestionChecklist: React.FC<QuestionChecklistProps> = ({
    questionsWithOptions,
    title,
    selectedQuestions,
    setSelectedQuestions,
    usersSelectedList,
    onClose,
    onApply,
    isLoading = false,
    questionnaireNames
}) => {
    const [expandedQuestions, setExpandedQuestions] = useState<Record<number, boolean>>({});
    const [expandedQuestionnaires, setExpandedQuestionnaires] = useState<Record<number, boolean>>({});
    const [hoveredSelectAllQnId, setHoveredSelectAllQnId] = useState<number | null>(null);

    // Initialize expanded state for questions
    useEffect(() => {
        const initialExpandedState: Record<number, boolean> = {};
        questionsWithOptions.questionsOptions.forEach(item => {
            initialExpandedState[item.Questions.Id] = false;
        });
        questionsWithOptions.questionsEmotions.forEach(item => {
            initialExpandedState[item.Questions.Id] = false;
        });
        setExpandedQuestions(initialExpandedState);
    }, [questionsWithOptions]);

    // Initialize expanded state for questionnaires (default: expanded)
    useEffect(() => {
        const qnIds = new Set<number>();
        questionsWithOptions.questionsOptions.forEach(item => {
            if (item.Questions?.QuestionnaireId != null) qnIds.add(item.Questions.QuestionnaireId);
        });
        questionsWithOptions.questionsEmotions.forEach(item => {
            if (item.Questions?.QuestionnaireId != null) qnIds.add(item.Questions.QuestionnaireId);
        });
        setExpandedQuestionnaires(prev => {
            const next = { ...prev };
            qnIds.forEach(id => {
                if (next[id] === undefined) next[id] = true;
            });
            return next;
        });
    }, [questionsWithOptions]);

    const toggleQuestionnaireExpand = useCallback((questionnaireId: number) => {
        setExpandedQuestionnaires(prev => ({
            ...prev,
            [questionnaireId]: !prev[questionnaireId]
        }));
    }, []);

    const buildSelectedForQuestionnaire = useCallback((qnId: number, byQuestionnaire: Record<number, { item: { Questions: { Id: number }; QuestionsOptions?: { Id: number }[]; QuestionsEmotions?: { Id: number }[] }; isEmotion: boolean }[]>) => {
        const obj: Record<number, number[]> = {};
        (byQuestionnaire[qnId] || []).forEach(({ item, isEmotion }) => {
            const opts = isEmotion ? (item.QuestionsEmotions || []) : (item.QuestionsOptions || []);
            obj[item.Questions.Id] = opts.map(o => o.Id);
        });
        return obj;
    }, []);

    const isQuestionnaireFullySelected = useCallback((qnId: number, byQuestionnaire: Record<number, { item: { Questions: { Id: number }; QuestionsOptions?: { Id: number }[]; QuestionsEmotions?: { Id: number }[] }; isEmotion: boolean }[]>) => {
        const items = byQuestionnaire[qnId] || [];
        if (items.length === 0) return false;
        return items.every(({ item, isEmotion }) => {
            const opts = isEmotion ? (item.QuestionsEmotions || []) : (item.QuestionsOptions || []);
            const sel = selectedQuestions[item.Questions.Id];
            return sel && sel.length === opts.length && opts.every(o => sel.includes(o.Id));
        });
    }, [selectedQuestions]);

    const handleQuestionnaireSelectAll = useCallback((qnId: number, byQuestionnaire: Record<number, { item: { Questions: { Id: number }; QuestionsOptions?: { Id: number }[]; QuestionsEmotions?: { Id: number }[] }; isEmotion: boolean }[]>) => {
        const toAdd = buildSelectedForQuestionnaire(qnId, byQuestionnaire);
        const isFull = isQuestionnaireFullySelected(qnId, byQuestionnaire);
        setSelectedQuestions(prev => {
            const next = { ...prev };
            if (isFull) {
                Object.keys(toAdd).forEach(qId => delete next[Number(qId)]);
            } else {
                Object.assign(next, toAdd);
            }
            return next;
        });
    }, [buildSelectedForQuestionnaire, isQuestionnaireFullySelected]);

    // Handlers
    const handleQuestionToggle = useCallback((questionId: number) => {
        setSelectedQuestions(prev => {
            const newSelection = { ...prev };
            if (newSelection[questionId]) {
                delete newSelection[questionId];
            } else {
                const question = questionsWithOptions.questionsOptions.find(q => q.Questions.Id === questionId);
                if (question) {
                    newSelection[questionId] = question.QuestionsOptions.map(opt => opt.Id);
                } else {
                    const questionEmotion = questionsWithOptions.questionsEmotions.find(q => q.Questions.Id === questionId);
                    if (questionEmotion) {
                        newSelection[questionId] = questionEmotion.QuestionsEmotions.map(opt => opt.Id);
                    }
                }
            }
            return newSelection;
        });
    }, [questionsWithOptions, setSelectedQuestions]);

    const handleOptionToggle = useCallback((questionId: number, optionId: number) => {
        const newSelection = { ...selectedQuestions };
        if (!newSelection[questionId]) {
            newSelection[questionId] = [];
        }
        if (newSelection[questionId].includes(optionId)) {
            newSelection[questionId] = newSelection[questionId].filter(id => id !== optionId);
            if (newSelection[questionId].length === 0) {
                delete newSelection[questionId];
            }
        } else {
            newSelection[questionId].push(optionId);
        }
        setSelectedQuestions(newSelection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSelectedQuestions]);

    const toggleExpand = useCallback((questionId: number) => {
        setExpandedQuestions(prev => ({
            ...prev,
            [questionId]: !prev[questionId]
        }));
    }, []);

    // Select All / Deselect All logic
    const allSelected = areAllSelected(selectedQuestions, questionsWithOptions);
    const selectAllBtnLabel = allSelected ? t('CANCEL_ALL_ELECTIONS') : t('SELECT_ALL_1');
    const handleSelectAllBtn = () => {
        if (allSelected) {
            setSelectedQuestions({});
        } else {
            setSelectedQuestions(buildAllSelectedObj(questionsWithOptions));
        }
    };

    const hasSelections = Object.keys(selectedQuestions).length > 0;

    return (
        <div className={styles.container}>
            <div className={styles.sidebarHeader}>
                <h2 className={styles.header}>{title}</h2>
                <button className={styles.closeBtn} onClick={onClose} />
            </div>

            <div className={styles.sidebarContent}>
                <h3 className={styles.header}>{t('CHOOSE_QUESTIONS_AND_ANSWERS')}</h3>

                {isLoading ? (
                    <Loader />
                ) : (
                    (() => {
                        type QItem = { Questions: { Id: number; QuestionnaireId?: number }; QuestionsOptions?: { Id: number }[]; QuestionsEmotions?: { Id: number }[] };
                        const allItems: { item: QItem; isEmotion: boolean }[] = [
                            ...questionsWithOptions.questionsOptions.map((item: QItem) => ({ item, isEmotion: false })),
                            ...(questionsWithOptions.questionsEmotions || []).map((item: QItem) => ({ item, isEmotion: true })),
                        ];
                        const byQuestionnaire = allItems.reduce<Record<number, { item: QItem; isEmotion: boolean }[]>>((acc, { item, isEmotion }) => {
                            const qnId = item.Questions?.QuestionnaireId ?? 0;
                            if (!acc[qnId]) acc[qnId] = [];
                            acc[qnId].push({ item, isEmotion });
                            return acc;
                        }, {});
                        const sortedQnIds = Object.keys(byQuestionnaire).map(Number).sort((a, b) => a - b);
                        const singleQuestionnaire = sortedQnIds.length <= 1;

                        return (
                            <>
                                {sortedQnIds.map(qnId => {
                                    const isExpanded = expandedQuestionnaires[qnId] !== false;
                                    const qnName = questionnaireNames?.[qnId] ?? (singleQuestionnaire && qnId > 0 ? title : `#${qnId}`);
                                    return (
                                        <div key={qnId} className={styles.questionnaireGroup}>
                                            <div className={styles.questionnaireGroupHeaderRow}>
                                                <button
                                                    type="button"
                                                    className={styles.questionnaireGroupHeader}
                                                    onClick={() => toggleQuestionnaireExpand(qnId)}
                                                    aria-expanded={isExpanded}
                                                >
                                                    <span className={styles.questionnaireToggle}>{isExpanded ? '−' : '+'}</span>
                                                    <span>{qnName}</span>
                                                </button>
                                                <div
                                                    className={styles.questionnaireSelectAllWrap}
                                                    onMouseEnter={() => setHoveredSelectAllQnId(qnId)}
                                                    onMouseLeave={() => setHoveredSelectAllQnId(null)}
                                                >
                                                    <button
                                                        type="button"
                                                        className={styles.questionnaireSelectAllBtn}
                                                        onClick={(e) => { e.stopPropagation(); handleQuestionnaireSelectAll(qnId, byQuestionnaire); }}
                                                        aria-label={isQuestionnaireFullySelected(qnId, byQuestionnaire) ? t('CANCEL_ALL_ELECTIONS') : t('SELECT_ALL_1')}
                                                    >
                                                        <span className={styles.questionnaireSelectAllIcon}>
                                                            {isQuestionnaireFullySelected(qnId, byQuestionnaire) ? <UnselectIcon /> : <SelectAllIcon />}
                                                        </span>
                                                    </button>
                                                    <Tooltip isShown={hoveredSelectAllQnId === qnId} position="bottom">
                                                        {isQuestionnaireFullySelected(qnId, byQuestionnaire) ? t('CANCEL_ALL_ELECTIONS') : t('SELECT_ALL_1')}
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <>
                                                    {byQuestionnaire[qnId].map(({ item, isEmotion }) => (
                                                        <QuestionItem
                                                            key={item.Questions.Id}
                                                            question={item.Questions}
                                                            options={isEmotion ? (item.QuestionsEmotions || []) : (item.QuestionsOptions || [])}
                                                            expanded={!!expandedQuestions[item.Questions.Id]}
                                                            selected={selectedQuestions[item.Questions.Id]}
                                                            onToggle={handleQuestionToggle}
                                                            onExpand={toggleExpand}
                                                            handleOptionToggle={handleOptionToggle}
                                                            labelKey={isEmotion ? 'NameHe' : 'Desc'}
                                                        />
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        );
                    })()
                )}
            </div>

            <div className={styles.sidebarFooter}>
                <button
                    className={styles.selectAllBtn}
                    onClick={handleSelectAllBtn}
                >
                    {selectAllBtnLabel}
                </button>
                <button
                    className={styles.applyButton}
                    onClick={() => {
                        onApply();
                        onClose();
                    }}
                    disabled={!hasSelections}
                >
                    {t('APPLY_AND_ANALYZE')}
                </button>
            </div>
        </div>
    );
};

export default QuestionChecklist;